/**
 * @file CouponForm.tsx
 *
 * The coupon create/edit form — a faithful replica of the Website Builder v2
 * CouponsTab form on this stack (react-intl, a custom controlled-state hook,
 * Gravity styling), re-implemented WITHOUT react-hook-form. Built on the shared
 * {@link FormColumnLayout} primitives (heading-above-card sections, `gv-label`
 * fields, native `gv-input-select` / `gv-input-textarea`, a `gv-input-date`
 * field with a calendar addon), matching the category and product forms. Owns the
 * editable state via {@link useCouponFormState}, validates on save, maps a known
 * server failure (`duplicate_code` / `invalid_amount`) to the relevant field, and
 * reports the assembled {@link ShopCouponPayload} to its parent. The discount-type
 * dropdown is a native `<select>` (the Gravity `Select` web component isn't
 * drivable under jsdom). Purely a view+state component: loading the seed and
 * persisting are the routed screen's job.
 */

import { useId } from 'react';
import type { RefObject } from 'react';
import { Link } from 'react-router-dom';
import { GeneralField, Icon, Toggle } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type { ShopCoupon, ShopCouponPayload } from '../../api/shop-types';
import { FormFooter } from '../../components/FormFooter';
import {
  FormColumnLayout,
  FormSection,
  FormSectionHeading,
  FormSectionCard,
} from '../../components/form-layout/FormColumnLayout';
import { discountTypeLabel } from '../coupon-list';
import { useCouponFormState } from './use-coupon-form-state';
import { buildCouponPayload } from './coupon-form-utils';
import { COUPON_DISCOUNT_TYPES, type DiscountType } from './coupon-form-types';
import styles from './CouponForm.module.css';

/** Which flow the form is serving; drives the title and save label. */
export type CouponFormMode = 'create' | 'edit';

interface CouponFormProps {
  coupon?: ShopCoupon;
  mode: CouponFormMode;
  currencySymbol: string;
  saving: boolean;
  saveError?: string;
  /** Machine-readable code of the last server failure, mapped to a field error. */
  serverErrorCode?: string | null;
  /** Clears the server error (called when the user edits the offending field). */
  onClearServerError: () => void;
  onSubmit: (payload: ShopCouponPayload) => void;
  onCancel: () => void;
}

/**
 * Renders the coupon form.
 *
 * @param props - Seed coupon, mode, currency symbol, save/server-error state, and handlers.
 *
 * @returns The coupon form.
 */
export function CouponForm({
  coupon,
  mode,
  currencySymbol,
  saving,
  saveError,
  serverErrorCode,
  onClearServerError,
  onSubmit,
  onCancel,
}: CouponFormProps) {
  const intl = useIntl();
  const codeId = useId();
  const typeId = useId();
  const amountId = useId();
  const descriptionId = useId();
  const expiresId = useId();
  const usageLimitId = useId();
  const minimumId = useId();
  const maximumId = useId();

  const { fields, patch, errors, validate, firstErrorRef } = useCouponFormState(coupon);

  const amountAddon = (
    <span className="gv-px-md gv-text-sm">
      {fields.discount_type === 'percent' ? '%' : currencySymbol}
    </span>
  );
  const currencyAddon = <span className="gv-px-md gv-text-sm">{currencySymbol}</span>;

  // Server failures map to a field; a client validation error for the same field
  // takes precedence (it reflects the latest input).
  const codeError =
    errors.codeError ??
    (serverErrorCode === 'coupon_code_conflict'
      ? intl.formatMessage(adminMessages.couponsCodeDuplicate)
      : undefined);
  const amountError =
    errors.amountError ??
    (serverErrorCode === 'coupon_invalid_amount'
      ? intl.formatMessage(adminMessages.couponsAmountInvalid)
      : undefined);
  const dateExpiresError =
    errors.dateExpiresError ??
    (serverErrorCode === 'coupon_invalid_window'
      ? intl.formatMessage(adminMessages.couponsExpiryInvalid)
      : undefined);
  const maximumAmountError =
    errors.maximumAmountError ??
    (serverErrorCode === 'coupon_invalid_spend'
      ? intl.formatMessage(adminMessages.couponsSpendInvalid)
      : undefined);

  const handleSave = (): void => {
    if (!validate()) {
      return;
    }
    onSubmit(buildCouponPayload(fields));
  };

  const title =
    mode === 'edit' ? adminMessages.couponsFormEditTitle : adminMessages.couponsFormAddTitle;
  const saveLabel =
    mode === 'edit' ? adminMessages.couponsFormSave : adminMessages.couponsFormCreate;

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <div className="gv-breadcrumbs">
          <Link to="/coupons">
            <Icon name="arrow_back" size="small" />
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
        </div>
        <h2 className="gv-heading-lg gv-m-0">
          <FormattedMessage {...title} />
        </h2>
      </header>

      <FormColumnLayout>
        <FormSection>
          <FormSectionHeading
            title={intl.formatMessage(adminMessages.couponsFormInformation)}
          />
          <FormSectionCard>
            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={codeId}>
                <span>
                  <FormattedMessage {...adminMessages.couponsFormCode} />
                </span>
              </label>
              <GeneralField
                id={codeId}
                inputRef={firstErrorRef as RefObject<HTMLInputElement>}
                value={fields.code}
                data-testid="coupon-form-code"
                placeholder={intl.formatMessage(adminMessages.couponsFormCodePlaceholder)}
                error={codeError}
                aria-label={intl.formatMessage(adminMessages.couponsFormCode)}
                onChange={(event) => {
                  patch({ code: event.target.value });
                  onClearServerError();
                }}
              />
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={typeId}>
                <span>
                  <FormattedMessage {...adminMessages.couponsFormType} />
                </span>
              </label>
              <div className="gv-input gv-input-select gv-w-full">
                <select
                  id={typeId}
                  value={fields.discount_type}
                  data-testid="coupon-form-discount-type"
                  aria-label={intl.formatMessage(adminMessages.couponsFormType)}
                  onChange={(event) =>
                    patch({ discount_type: event.target.value as DiscountType })
                  }
                >
                  {COUPON_DISCOUNT_TYPES.map((type) => {
                    const label = discountTypeLabel(type);
                    const text = label ? intl.formatMessage(label) : type;
                    const suffix = type === 'percent' ? '%' : currencySymbol;
                    return (
                      <option key={type} value={type}>
                        {`${text} (${suffix})`}
                      </option>
                    );
                  })}
                </select>
                <Icon name="expand_more" size="small" />
              </div>
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={amountId}>
                <span>
                  <FormattedMessage {...adminMessages.couponsFormAmount} />
                </span>
              </label>
              <GeneralField
                id={amountId}
                value={fields.amount}
                inputMode="decimal"
                addon={amountAddon}
                error={amountError}
                data-testid="coupon-form-amount"
                aria-label={intl.formatMessage(adminMessages.couponsFormAmount)}
                onChange={(event) => {
                  patch({ amount: event.target.value });
                  onClearServerError();
                }}
              />
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={descriptionId}>
                <span>
                  <FormattedMessage {...adminMessages.couponsFormDescription} />
                </span>
                <span className="gv-label-optional">
                  <FormattedMessage {...adminMessages.formOptional} />
                </span>
              </label>
              <textarea
                id={descriptionId}
                className="gv-input gv-input-textarea gv-w-full"
                value={fields.description}
                rows={3}
                data-testid="coupon-form-description"
                aria-label={intl.formatMessage(adminMessages.couponsFormDescription)}
                onChange={(event) => patch({ description: event.target.value })}
              />
            </div>
          </FormSectionCard>
        </FormSection>

        <FormSection>
          <FormSectionHeading
            title={intl.formatMessage(adminMessages.couponsFormLimitations)}
          />
          <FormSectionCard>
            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={expiresId}>
                <span>
                  <FormattedMessage {...adminMessages.couponsFormExpires} />
                </span>
                <span className="gv-label-optional">
                  <FormattedMessage {...adminMessages.formOptional} />
                </span>
              </label>
              <div className="gv-input gv-w-full gv-input-date gv-with-addon">
                <input
                  id={expiresId}
                  type="date"
                  data-testid="coupon-form-expires"
                  value={fields.date_expires}
                  aria-label={intl.formatMessage(adminMessages.couponsFormExpires)}
                  onChange={(event) => {
                    patch({ date_expires: event.target.value });
                    onClearServerError();
                  }}
                />
                <div className="gv-addon" aria-hidden="true">
                  <Icon name="calendar_month" size="small" />
                </div>
              </div>
              {dateExpiresError && (
                <span className="gv-text-sm gv-state-critical" role="alert">
                  {dateExpiresError}
                </span>
              )}
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={usageLimitId}>
                <span>
                  <FormattedMessage {...adminMessages.couponsFormUsageLimit} />
                </span>
                <span className="gv-label-optional">
                  <FormattedMessage {...adminMessages.formOptional} />
                </span>
              </label>
              <GeneralField
                id={usageLimitId}
                value={fields.usage_limit}
                inputMode="numeric"
                placeholder={intl.formatMessage(adminMessages.couponsFormUsageLimitPlaceholder)}
                error={errors.usageLimitError}
                data-testid="coupon-form-usage-limit"
                aria-label={intl.formatMessage(adminMessages.couponsFormUsageLimit)}
                onChange={(event) => patch({ usage_limit: event.target.value })}
              />
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={minimumId}>
                <span>
                  <FormattedMessage {...adminMessages.couponsFormMinimumAmount} />
                </span>
                <span className="gv-label-optional">
                  <FormattedMessage {...adminMessages.formOptional} />
                </span>
              </label>
              <GeneralField
                id={minimumId}
                value={fields.minimum_amount}
                inputMode="decimal"
                addon={currencyAddon}
                error={errors.minimumAmountError}
                data-testid="coupon-form-minimum-amount"
                aria-label={intl.formatMessage(adminMessages.couponsFormMinimumAmount)}
                onChange={(event) => patch({ minimum_amount: event.target.value })}
              />
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={maximumId}>
                <span>
                  <FormattedMessage {...adminMessages.couponsFormMaximumAmount} />
                </span>
                <span className="gv-label-optional">
                  <FormattedMessage {...adminMessages.formOptional} />
                </span>
              </label>
              <GeneralField
                id={maximumId}
                value={fields.maximum_amount}
                inputMode="decimal"
                addon={currencyAddon}
                error={maximumAmountError}
                data-testid="coupon-form-maximum-amount"
                aria-label={intl.formatMessage(adminMessages.couponsFormMaximumAmount)}
                onChange={(event) => {
                  patch({ maximum_amount: event.target.value });
                  onClearServerError();
                }}
              />
            </div>

            <div
              className="gv-flex gv-gap-sm gv-items-start"
              data-testid="coupon-form-individual-use"
            >
              <div className={`gv-pt-xs ${styles.noShrink}`}>
                <Toggle
                  condensed
                  checked={fields.individual_use}
                  onChange={(checked) => patch({ individual_use: checked })}
                />
              </div>
              <div className="gv-flex gv-flex-col gv-gap-xs">
                <span className="gv-text-sm">
                  <FormattedMessage {...adminMessages.couponsFormIndividualUse} />
                </span>
                <span className="gv-caption-lg gv-text-on-alternative">
                  <FormattedMessage {...adminMessages.couponsFormIndividualUseDesc} />
                </span>
              </div>
            </div>

            <div
              className="gv-flex gv-gap-sm gv-items-start"
              data-testid="coupon-form-free-shipping"
            >
              <div className={`gv-pt-xs ${styles.noShrink}`}>
                <Toggle
                  condensed
                  checked={fields.free_shipping}
                  onChange={(checked) => patch({ free_shipping: checked })}
                />
              </div>
              <div className="gv-flex gv-flex-col gv-gap-xs">
                <span className="gv-text-sm">
                  <FormattedMessage {...adminMessages.couponsFormFreeShipping} />
                </span>
                <span className="gv-caption-lg gv-text-on-alternative">
                  <FormattedMessage {...adminMessages.couponsFormFreeShippingDesc} />
                </span>
              </div>
            </div>
          </FormSectionCard>
        </FormSection>

        {saveError && (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="coupon-form-error"
          >
            {saveError}
          </p>
        )}

        <FormFooter>
          <button
            type="button"
            className="gv-button gv-button-cancel"
            data-testid="coupon-form-cancel"
            onClick={onCancel}
            disabled={saving}
          >
            <FormattedMessage {...adminMessages.formBack} />
          </button>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="coupon-form-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <FormattedMessage {...adminMessages.couponsFormSaving} />
            ) : (
              <FormattedMessage {...saveLabel} />
            )}
          </button>
        </FormFooter>
      </FormColumnLayout>
    </div>
  );
}
